// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js';

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      lock: async (_name, _acquireTimeout, fn) => fn(),
    },
    realtime: { params: { eventsPerSecond: 10 } },
  }
);

// ── Auth helpers ─────────────────────────────────────────────

export const signUp = async (email, password, name) => {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { name } },
  });
  if (error) throw error;
  return data;
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const getSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

export const onAuthChange = (cb) => {
  const { data } = supabase.auth.onAuthStateChange(cb);
  return data.subscription;
};

// ── Profile helpers ──────────────────────────────────────────

// Récupère tous les profils d'un utilisateur
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error && error.code !== 'PGRST116') throw error;
  // Retourne le premier profil pour compatibilité
  return data && data.length > 0 ? data[0] : null;
};

// Récupère tous les profils d'un utilisateur
export const getUserProfiles = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const upsertProfile = async (profile) => {
  // Si le profil a un id existant, on le met à jour
  // Sinon on en crée un nouveau
  if (profile.id && !profile._isNew) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...profile, id: undefined })
      .eq('id', profile.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { _isNew, ...profileData } = profile;
    const { data, error } = await supabase
      .from('profiles')
      .insert({ ...profileData, id: undefined })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};

export const uploadAvatar = async (profileId, file) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non authentifié');
  const ext = file.name.split('.').pop().toLowerCase();
  const path = `${user.id}/${profileId}.${ext}`;
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl + '?t=' + Date.now();
};

export const uploadGalleryPhoto = async (profileId, file) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non authentifié');
  const ext = file.name.split('.').pop().toLowerCase();
  const path = `${user.id}/${profileId}/gallery/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl;
};

export const createProfile = async (profile) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert(profile)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getAllProfiles = async (filters = {}) => {
  let query = supabase.from('profiles').select('*');
  if (filters.type) query = query.eq('type', filters.type);
  if (filters.search) query = query.or(
    `name.ilike.%${filters.search}%,genre.ilike.%${filters.search}%,region.ilike.%${filters.search}%,bio.ilike.%${filters.search}%`
  );
  query = query.order('created_at', { ascending: false });
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// ── Message helpers ──────────────────────────────────────────

export const getMessages = async (userId) => {
  // Récupère tous les profile IDs de cet utilisateur
  const { data: userProfiles } = await supabase
    .from('profiles')
    .select('id')
    .eq('user_id', userId);
  
  const profileIds = userProfiles ? userProfiles.map(p => p.id) : [userId];
  const allIds = [...new Set([userId, ...profileIds])];
  
  const { data, error } = await supabase
    .from('messages')
.select('*')
    .or(allIds.map(id => `from_id.eq.${id},to_id.eq.${id}`).join(','))
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};
export const sendMessage = async (fromId, toId, subject, body, hasInvite = false, inviteId = null) => {
  const { data, error } = await supabase
    .from('messages')
    .insert({ from_id: fromId, to_id: toId, subject, body, has_invite: hasInvite, invite_id: inviteId })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const markMessageRead = async (messageId) => {
  await supabase.from('messages').update({ read: true }).eq('id', messageId);
};

// ── Chat helpers ─────────────────────────────────────────────

export const getRoomId = (uid1, uid2) => [uid1, uid2].sort().join('_');

export const getChatMessages = async (roomId) => {
  const { data, error } = await supabase
    .from('chats')
    .select('*, sender:sender_id(id,name,avatar)')
    .eq('room_id', roomId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const sendChatMessage = async (roomId, senderId, body) => {
  const { data, error } = await supabase
    .from('chats')
    .insert({ room_id: roomId, sender_id: senderId, body })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const subscribeToChatRoom = (roomId, callback) => {
  return supabase
    .channel('chat_' + roomId)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'chats',
      filter: 'room_id=eq.' + roomId,
    }, callback)
    .subscribe();
};

// ── Invitation helpers ───────────────────────────────────────

export const createInvitation = async (inv) => {
  const { data, error } = await supabase
    .from('invitations')
    .insert(inv)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getMyInvitations = async (userId) => {
  const { data: userProfiles } = await supabase
    .from('profiles')
    .select('id')
    .eq('user_id', userId);

  const profileIds = userProfiles ? userProfiles.map(p => p.id) : [];
  const allIds = [...new Set([userId, ...profileIds])];

  const orFilter = allIds.map(id => `organizer_id.eq.${id},invitee_id.eq.${id}`).join(',');

  const { data, error } = await supabase
    .from('invitations')
    .select('*')
    .or(orFilter)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};
export const respondToInvitation = async (invId, status, signature = null) => {
  const update = { status };
  if (signature) { update.invitee_signature = signature; update.legal_accepted_by_invitee = true; }
  
  const { data, error } = await supabase
    .from('invitations')
    .update(update)
    .eq('id', invId)
    .select()
    .single();
  if (error) throw error;

  // Calendar entries for both parties are created by a DB trigger on invitations
  // (create_booking_calendar_entries) — no client-side insert needed.
  return data;
};
// ── Campaign helpers ─────────────────────────────────────────

export const createCampaign = async (campaign) => {
  const { data, error } = await supabase
    .from('campaigns')
    .insert(campaign)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getMyCampaigns = async (userId) => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

// ── Calendar helpers ─────────────────────────────────────────

export const getMyCalendar = async (userId) => {
  const { data, error } = await supabase
    .from('calendar_entries')
    .select('*')
    .eq('user_id', userId)
    .order('date_start', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const addCalendarEntry = async (entry) => {
  const { data, error } = await supabase
    .from('calendar_entries')
    .insert(entry)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteCalendarEntry = async (id) => {
  const { error } = await supabase.from('calendar_entries').delete().eq('id', id);
  if (error) throw error;
};

export const updateCalendarEntry = async (id, fields) => {
  const { data, error } = await supabase
    .from('calendar_entries')
    .update(fields)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ── Artist tier helpers ──────────────────────────────────────

export const getFanSubscription = async (fanId, artistId) => {
  const { data, error } = await supabase
    .from('fan_artist_subscriptions')
    .select('*')
    .eq('fan_id', fanId)
    .eq('artist_id', artistId)
    .eq('status', 'active')
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const subscribeToArtist = async (fanId, artistId, plan = 'monthly') => {
  const expiresAt = new Date();
  expiresAt.setMonth(expiresAt.getMonth() + (plan === 'annual' ? 12 : 1));
  const { data, error } = await supabase
    .from('fan_artist_subscriptions')
    .upsert({ fan_id: fanId, artist_id: artistId, plan, status: 'active', expires_at: expiresAt.toISOString() }, { onConflict: 'fan_id,artist_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const unsubscribeFromArtist = async (fanId, artistId) => {
  const { error } = await supabase
    .from('fan_artist_subscriptions')
    .update({ status: 'cancelled' })
    .eq('fan_id', fanId)
    .eq('artist_id', artistId);
  if (error) throw error;
};

export const getRepertoryItems = async (artistId) => {
  const { data, error } = await supabase
    .from('repertory_items')
    .select('*')
    .eq('artist_id', artistId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const addRepertoryItem = async (item) => {
  const { data, error } = await supabase
    .from('repertory_items')
    .insert(item)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteRepertoryItem = async (itemId) => {
  const { error } = await supabase
    .from('repertory_items')
    .delete()
    .eq('id', itemId);
  if (error) throw error;
};

export const getArtistProfile = async (profileId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  if (error) throw error;
  return data;
};

export const getPublicEvents = async () => {
  const { data: events, error } = await supabase
    .from('calendar_entries')
    .select('*')
    .eq('visibility', 'public')
    .gt('date_start', new Date().toISOString())
    .order('date_start', { ascending: true });
  if (error) throw error;
  if (!events || events.length === 0) return [];
  const userIds = [...new Set(events.map(e => e.user_id))];
  const { data: profileRows } = await supabase
    .from('profiles')
    .select('id, name, avatar, type, genre, region, lat, lng')
    .in('id', userIds);
  const pm = {};
  (profileRows || []).forEach(p => { pm[p.id] = p; });
  return events.map(e => ({ ...e, profile: pm[e.user_id] || null }));
};

// ── Booking Agent ─────────────────────────────────────────────
export const createInviteToken = async (agentId, agentName, profileType) => {
  const { data, error } = await supabase
    .from('invite_tokens')
    .insert({ agent_id: agentId, agent_name: agentName, profile_type: profileType })
    .select().single();
  if (error) throw error;
  return data;
};

export const getInviteToken = async (token) => {
  const { data, error } = await supabase
    .from('invite_tokens')
    .select('*')
    .eq('token', token)
    .single();
  if (error) throw error;
  return data;
};

export const claimInviteToken = async (token, profileId) => {
  const { data: tk, error: e1 } = await supabase
    .from('invite_tokens').select('agent_id').eq('token', token).is('used_by', null).single();
  if (e1 || !tk) throw new Error('Lien invalide ou déjà utilisé');
  const { error: e2 } = await supabase
    .from('invite_tokens').update({ used_by: profileId, used_at: new Date().toISOString() }).eq('token', token);
  if (e2) throw e2;
  const { error: e3 } = await supabase
    .from('profiles').update({ agent_id: tk.agent_id }).eq('id', profileId);
  if (e3) throw e3;
};

export const getAgentClients = async (agentId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, type, avatar, avatar_url, region, created_at')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getAgentTokens = async (agentId) => {
  const { data, error } = await supabase
    .from('invite_tokens')
    .select('*')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data || [];
};

